import React, { useState } from "react";
import { toast } from "sonner";
import { Calendar, Clock, X, Loader2, Edit3 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * AdmissionScheduleCard — consultor agenda data/hora da internação com a
 * família após a clínica confirmar reserva. Pode reagendar ou cancelar.
 * Só aparece pra role="consultor" ou "admin".
 */
const AdmissionScheduleCard = ({ caseObj, role, onDone }) => {
  const schedule = caseObj?.admission_schedule;
  const [editing, setEditing] = useState(!schedule);
  const [dt, setDt] = useState(schedule?.scheduled_at?.slice(0, 16) || "");
  const [notes, setNotes] = useState(schedule?.notes || "");
  const [busy, setBusy] = useState(false);

  if (role !== "consultor" && role !== "admin") return null;
  // Só aparece após clínica confirmar reserva
  if (!["reserva_confirmada", "concluido"].includes(caseObj?.status)) return null;
  // Se já teve internação confirmada, esconde (fase superada)
  if (caseObj?.arrival?.confirmed) return null;

  const save = async () => {
    if (!dt) { toast.error("Escolha data e hora"); return; }
    setBusy(true);
    try {
      await api.put(`/consultant/cases/${caseObj.id}/admission-schedule`, {
        scheduled_at: `${dt}:00`,
        notes: notes || null,
      });
      toast.success(schedule ? "Agendamento atualizado" : "Internação agendada");
      setEditing(false);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao agendar"); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancelar o agendamento? A família e a clínica serão notificadas.")) return;
    setBusy(true);
    try {
      await api.delete(`/consultant/cases/${caseObj.id}/admission-schedule`);
      toast.success("Agendamento cancelado");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  // Modo visualização (agendado)
  if (schedule && !editing) {
    return (
      <section className="bg-sky-50 border border-sky-300 rounded-xl p-5 space-y-3" data-testid="admission-schedule-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sky-900 font-display font-bold text-lg">
            <Calendar className="w-5 h-5" /> Internação agendada
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditing(true)} data-testid="admission-reschedule-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-sky-700 text-sky-800 rounded-md text-xs font-semibold hover:bg-sky-700 hover:text-white transition-colors">
              <Edit3 className="w-3.5 h-3.5" /> Reagendar
            </button>
            <button onClick={cancel} disabled={busy} data-testid="admission-cancel-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-400 text-red-700 rounded-md text-xs font-semibold hover:bg-red-50 disabled:opacity-50 transition-colors">
              <X className="w-3.5 h-3.5" /> Cancelar
            </button>
          </div>
        </div>
        <div className="text-sky-900">
          <div className="text-2xl font-display font-bold">{fmt(schedule.scheduled_at)}</div>
          <div className="text-xs text-sky-700 mt-1">
            Agendado por {schedule.scheduled_by_name} · {fmt(schedule.scheduled_at_iso)}
            {schedule.reschedule_count > 0 && ` · ${schedule.reschedule_count}ª remarcação`}
          </div>
        </div>
        {schedule.notes && (
          <div className="text-sm text-sky-900 bg-white border border-sky-200 rounded-md p-3">
            <span className="text-xs text-sky-600 font-semibold uppercase tracking-widest">Observações · </span>
            {schedule.notes}
          </div>
        )}
        <div className="text-xs text-sky-700 pt-2 border-t border-sky-200">
          A clínica confirmará a chegada quando a família comparecer nesse horário.
        </div>
      </section>
    );
  }

  // Modo edição
  return (
    <section className="bg-white border-2 border-dashed border-[#0B2B62] rounded-xl p-5 space-y-3" data-testid="admission-schedule-form">
      <div className="flex items-center gap-2 text-[#0B2B62] font-display font-bold text-lg">
        <Calendar className="w-5 h-5" /> {schedule ? "Reagendar internação" : "Agendar internação com a família"}
      </div>
      <p className="text-xs text-slate-600">
        Combine a data e hora prevista da internação com a família. A clínica só confirma a chegada quando o paciente comparecer.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Data e hora</div>
          <input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)}
            data-testid="admission-datetime"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Observações (opcional)</div>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
            data-testid="admission-notes"
            placeholder="Ex: buscar pela manhã, RG em mãos..."
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
        </label>
      </div>
      <div className="flex gap-2 pt-2">
        <button onClick={save} disabled={busy || !dt} data-testid="admission-save-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold hover:bg-[#08204a] disabled:opacity-50">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvando…</> : <><Clock className="w-4 h-4" /> {schedule ? "Salvar remarcação" : "Confirmar agendamento"}</>}
        </button>
        {schedule && (
          <button onClick={() => setEditing(false)} disabled={busy}
            className="px-4 py-2 border border-slate-300 text-slate-700 rounded-md text-sm hover:bg-slate-50">
            Descartar
          </button>
        )}
      </div>
    </section>
  );
};

export default AdmissionScheduleCard;
